import { useState } from "react";
import { motion } from "framer-motion";
import { TESTIMONIALS, WHATSAPP_LINK } from "../../lib/constants";
import { Star, Heart, Share2, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState(TESTIMONIALS);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleLike = (id) => {
    setTestimonials((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, likes: t.likes + 1 } : t
      )
    );
    toast.success("¡Gracias por tu like!");
  };

  const handleShare = (testimonial) => {
    const text = `"${testimonial.text}" - ${testimonial.name} sobre @urbanbrownies1`;
    
    if (navigator.share) {
      navigator.share({
        title: "Urban Brownies - Testimonio",
        text: text,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Testimonio copiado para compartir");
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section
      id="testimonios"
      className="py-20 md:py-32 bg-[#2D1810]"
      data-testid="testimonials-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#C88037] font-medium text-sm uppercase tracking-wider">
            Lo que dicen nuestros clientes
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-white mt-2 mb-4">
            Historias <span className="italic text-[#C88037]">dulces</span>
          </h2>
          <p className="text-white/70 max-w-xl mx-auto">
            No somos nosotros quienes lo decimos. Son ellos, los que ya probaron 
            y no pudieron resistirse a contarlo.
          </p>
        </motion.div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="testimonial-card bg-[#422418] rounded-2xl p-6 relative"
            >
              {/* Quote Icon */}
              <Quote
                className="absolute top-4 right-4 w-8 h-8 text-[#C88037]/20"
                strokeWidth={1.5}
              />

              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-[#C88037] text-[#C88037]"
                    strokeWidth={1.5}
                  />
                ))}
              </div>

              {/* Text */}
              <p className="text-white/90 text-sm mb-4 line-clamp-4">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">{testimonial.name}</p>
                  <p className="text-white/50 text-xs">{testimonial.date}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => handleLike(testimonial.id)}
                  className="flex items-center gap-1 text-white/60 hover:text-[#D9381E] transition-colors heart-button"
                  data-testid={`like-testimonial-${testimonial.id}`}
                >
                  <Heart className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">{testimonial.likes}</span>
                </button>
                <button
                  onClick={() => handleShare(testimonial)}
                  className="flex items-center gap-1 text-white/60 hover:text-[#C88037] transition-colors"
                  data-testid={`share-testimonial-${testimonial.id}`}
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm">Compartir</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden relative">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-[#422418] rounded-2xl p-6"
          >
            <div className="flex gap-1 mb-4">
              {Array.from({ length: testimonials[currentIndex].rating }).map((_, i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-[#C88037] text-[#C88037]"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <p className="text-white/90 text-lg mb-4">
              "{testimonials[currentIndex].text}"
            </p>
            <div>
              <p className="text-white font-semibold">{testimonials[currentIndex].name}</p>
              <p className="text-white/50 text-sm">{testimonials[currentIndex].date}</p>
            </div>
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-white/10">
              <button
                onClick={() => handleLike(testimonials[currentIndex].id)}
                className="flex items-center gap-2 text-white/60 hover:text-[#D9381E] transition-colors"
              >
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                <span>{testimonials[currentIndex].likes}</span>
              </button>
              <button
                onClick={() => handleShare(testimonials[currentIndex])}
                className="flex items-center gap-2 text-white/60 hover:text-[#C88037] transition-colors"
              >
                <Share2 className="w-5 h-5" strokeWidth={1.5} />
                <span>Compartir</span>
              </button>
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={prevSlide}
              className="p-2 bg-[#422418] text-white rounded-full hover:bg-[#C88037] transition-colors"
              data-testid="prev-testimonial"
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    i === currentIndex ? "bg-[#C88037]" : "bg-white/30"
                  }`}
                />
              ))}
            </div>
            <button
              onClick={nextSlide}
              className="p-2 bg-[#422418] text-white rounded-full hover:bg-[#C88037] transition-colors"
              data-testid="next-testimonial"
            >
              <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-white/60 mb-4">¿Ya probaste Urban Brownies?</p>
          <a
            href={`${WHATSAPP_LINK}?text=${encodeURIComponent("¡Hola! Quiero dejar una reseña sobre mis Urban Brownies 🍫")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#C88037] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E5984A] transition-colors"
            data-testid="leave-review-btn"
          >
            <Star className="w-5 h-5" strokeWidth={1.5} />
            Dejar mi Reseña
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
